/*
 * Colorize a grayscale/monochrome image by using source luminance as a shade map.
 */
const filter = (fabric) => {

    const filters = fabric.Image.filters;
    const createClass = fabric.util.createClass;

    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    const smoothstep = (edge0, edge1, value) => {
        const x = clamp((value - edge0) / (edge1 - edge0), 0, 1);
        return x * x * (3 - (2 * x));
    };

    filters.ColorizeLuma = createClass(filters.BaseFilter, /** @lends fabric.Image.filters.ColorizeLuma.prototype */ {

        type: 'ColorizeLuma',

        fragmentSource: `
            precision highp float;
            uniform sampler2D uTexture;
            uniform vec4 color;
            uniform float strength;
            uniform float shadows;
            uniform float highlights;
            uniform float highlightMix;
            uniform float highlightStart;
            varying vec2 vTexCoord;

            float mapLuma(float luma) {
                return shadows + (luma * (highlights - shadows));
            }

            void main() {
                vec4 src = texture2D(uTexture, vTexCoord);
                float luma = dot(src.rgb, vec3(0.299, 0.587, 0.114));
                float shade = clamp(mapLuma(luma), 0.0, 1.0);
                vec3 colorized = color.rgb * shade;
                float highlightAmount = smoothstep(highlightStart, 1.0, luma) * highlightMix;

                colorized = mix(colorized, vec3(1.0), highlightAmount);
                colorized = mix(src.rgb, colorized, strength);

                gl_FragColor = vec4(colorized, src.a);
            }
        `,

        color: '#ffffff',
        strength: 1,
        shadows: 0,
        highlights: 1,
        highlightMix: 0,
        highlightStart: 0.75,

        applyTo2d: function(options) {

            var imageData = options.imageData,
                data = imageData.data,
                i, len = data.length,
                destination = new fabric.Color(this.color).getSource(),
                strength = clamp(this.strength === undefined ? 1 : this.strength, 0, 1),
                shadows = clamp(this.shadows === undefined ? 0 : this.shadows, 0, 1),
                highlights = clamp(this.highlights === undefined ? 1 : this.highlights, 0, 1),
                highlightMix = clamp(this.highlightMix === undefined ? 0 : this.highlightMix, 0, 1),
                highlightStart = clamp(this.highlightStart === undefined ? 0.75 : this.highlightStart, 0, 1);

            for (i = 0; i < len; i += 4) {
                var luma = ((data[i] * 0.299) + (data[i + 1] * 0.587) + (data[i + 2] * 0.114)) / 255;
                var shade = clamp(shadows + (luma * (highlights - shadows)), 0, 1);
                var highlightAmount = smoothstep(highlightStart, 1, luma) * highlightMix;
                var r = destination[0] * shade;
                var g = destination[1] * shade;
                var b = destination[2] * shade;

                r = (r * (1 - highlightAmount)) + (255 * highlightAmount);
                g = (g * (1 - highlightAmount)) + (255 * highlightAmount);
                b = (b * (1 - highlightAmount)) + (255 * highlightAmount);

                data[i] = Math.round((data[i] * (1 - strength)) + (r * strength));
                data[i + 1] = Math.round((data[i + 1] * (1 - strength)) + (g * strength));
                data[i + 2] = Math.round((data[i + 2] * (1 - strength)) + (b * strength));
            }

        },

        getUniformLocations: function(gl, program) {
            return {
                uColor: gl.getUniformLocation(program, 'color'),
                uStrength: gl.getUniformLocation(program, 'strength'),
                uShadows: gl.getUniformLocation(program, 'shadows'),
                uHighlights: gl.getUniformLocation(program, 'highlights'),
                uHighlightMix: gl.getUniformLocation(program, 'highlightMix'),
                uHighlightStart: gl.getUniformLocation(program, 'highlightStart'),
            };
        },

        sendUniformData: function(gl, uniformLocations) {
            var destination = new fabric.Color(this.color).getSource();

            destination[0] /= 255;
            destination[1] /= 255;
            destination[2] /= 255;

            gl.uniform4fv(uniformLocations.uColor, destination);
            gl.uniform1f(uniformLocations.uStrength, this.strength === undefined ? 1 : this.strength);
            gl.uniform1f(uniformLocations.uShadows, this.shadows === undefined ? 0 : this.shadows);
            gl.uniform1f(uniformLocations.uHighlights, this.highlights === undefined ? 1 : this.highlights);
            gl.uniform1f(uniformLocations.uHighlightMix, this.highlightMix === undefined ? 0 : this.highlightMix);
            gl.uniform1f(uniformLocations.uHighlightStart, this.highlightStart === undefined ? 0.75 : this.highlightStart);
        },

        isNeutralState: function() {
            return this.strength === 0;
        },

        toObject: function() {
            return fabric.util.object.extend(this.callSuper('toObject'), {
                color: this.color,
                strength: this.strength,
                shadows: this.shadows,
                highlights: this.highlights,
                highlightMix: this.highlightMix,
                highlightStart: this.highlightStart,
            });
        }
    });

    fabric.Image.filters.ColorizeLuma.fromObject = fabric.Image.filters.BaseFilter.fromObject;

};

export default filter;
